import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { CTAPanel } from "../components/CTAPanel";
import { PILLARS } from "../data/content";

export default function Services() {
  return (
    <>
      <section className="pt-[72px] hero-grid-bg" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-16 sm:pt-28 sm:pb-20">
          <Reveal>
            <p className="text-indigo font-semibold text-sm uppercase tracking-widest mb-4">Services</p>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-ink max-w-3xl leading-[1.06]">
              Launch it. Grow it. <span className="text-gradient">Automate it.</span>
            </h1>
            <p className="mt-6 text-slate text-base sm:text-lg max-w-2xl leading-relaxed">
              Seventeen services, three pillars, one Sydney team. No juggling five different freelancers — and every step explained in plain English.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {PILLARS.map((p) => (
                <a
                  key={p.id}
                  href={`#${p.id}`}
                  data-testid={`services-jump-${p.id}`}
                  className="inline-flex items-center gap-2 bg-white border border-ink/10 rounded-full px-5 py-2.5 text-sm font-semibold text-ink hover:border-indigo hover:text-indigo transition-colors duration-200"
                >
                  <p.icon className="w-4 h-4 text-indigo" /> {p.name}
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {PILLARS.map((pillar, pi) => (
        <section
          key={pillar.id}
          id={pillar.id}
          data-testid={`pillar-section-${pillar.id}`}
          className={`px-5 sm:px-8 py-20 sm:py-24 ${pi === 1 ? "bg-ink grain" : ""}`}
        >
          <div className={`max-w-7xl mx-auto ${pi === 1 ? "relative z-10" : ""}`}>
            <Reveal>
              <div className="flex items-center gap-5 mb-4">
                <span className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
                  pi === 1 ? "bg-white/10" : "bg-gradient-to-br from-indigo/10 to-mint/10"
                }`}>
                  <pillar.icon className={`w-6 h-6 ${pi === 1 ? "text-mint" : "text-indigo"}`} />
                </span>
                <div>
                  <p className={`font-semibold text-sm uppercase tracking-widest ${pi === 1 ? "text-mint" : "text-indigo"}`}>
                    Pillar {String(pi + 1).padStart(2, "0")}
                  </p>
                  <h2 className={`font-display font-bold text-3xl sm:text-4xl ${pi === 1 ? "text-white" : "text-ink"}`}>
                    {pillar.name}
                  </h2>
                </div>
              </div>
              <p className={`text-base sm:text-lg max-w-2xl mb-12 ${pi === 1 ? "text-white/55" : "text-slate"}`}>
                {pillar.tagline}
              </p>
            </Reveal>

            <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {pillar.services.map((s) => (
                <StaggerItem key={s.title}>
                  <article
                    data-testid={`service-detail-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className={`group h-full rounded-[2rem] p-8 sm:p-10 transition-[box-shadow,transform] duration-300 hover:-translate-y-1 ${
                      pi === 1
                        ? "bg-white/5 border border-white/10 hover:bg-white/8"
                        : "bg-white border border-ink/5 shadow-sm hover:shadow-[0_24px_56px_rgba(20,22,46,0.09)]"
                    }`}
                  >
                    <div className="flex items-center gap-4 mb-5">
                      <span className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                        pi === 1
                          ? "bg-mint/15 group-hover:bg-mint"
                          : "bg-gradient-to-br from-indigo/10 to-mint/10 group-hover:from-indigo group-hover:to-mint"
                      }`}>
                        <s.icon className={`w-5 h-5 transition-colors duration-300 ${
                          pi === 1 ? "text-mint group-hover:text-ink" : "text-indigo group-hover:text-white"
                        }`} />
                      </span>
                      <h3 className={`font-display font-semibold text-lg sm:text-xl ${pi === 1 ? "text-white" : "text-ink"}`}>
                        {s.title}
                      </h3>
                    </div>
                    <p className={`text-sm sm:text-base leading-relaxed mb-6 ${pi === 1 ? "text-white/60" : "text-slate"}`}>
                      {s.detail}
                    </p>
                    <ul className="grid sm:grid-cols-2 gap-2.5">
                      {s.deliverables.map((d) => (
                        <li key={d} className={`flex items-start gap-2.5 text-sm ${pi === 1 ? "text-white/75" : "text-ink/75"}`}>
                          <Check className={`w-4 h-4 mt-0.5 shrink-0 ${pi === 1 ? "text-mint" : "text-indigo"}`} /> {d}
                        </li>
                      ))}
                    </ul>
                  </article>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>
      ))}

      <section className="px-5 sm:px-8 py-16 text-center" data-testid="services-bottom">
        <Reveal>
          <Link
            to="/contact"
            data-testid="services-bottom-cta"
            className="inline-flex items-center gap-2 bg-indigo text-white font-semibold px-8 py-4 rounded-full shadow-[0_12px_32px_rgba(91,95,239,0.35)] hover:bg-ink transition-colors duration-300 group"
          >
            Not sure what you need? Let's talk
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>

      <CTAPanel title="One team. One invoice. Zero headaches." />
    </>
  );
}
